import React, { useState } from "react";
import { useWalletClient, usePublicClient } from "wagmi";
import { adminApi } from "../../services/api";
import AppleTreeABI from "../../contracts/AppleTree.json";

/**
 * Componente per revocare/eliminare una certificazione ESG on-chain
 * Flow:
 * 1. Show confirmation dialog with reason input
 * 2. Sign on-chain tx: decertifyCompany(companyWallet)
 * 3. Wait for transaction receipt
 * 4. Call backend POST /admin/esg/certifications/revoke with requestId, txHash, and reason
 * 5. Update UI state
 */
const AdminRevokeButton = ({ 
  requestId, 
  companyWalletAddress,
  adminKey, 
  walletAddress,
  onSuccess,
  onError 
}) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  
  const [revoking, setRevoking] = useState(false);
  const [step, setStep] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [reason, setReason] = useState("");

  // Indirizzo del contratto AppleTree
  const APPLETREE_CONTRACT = process.env.REACT_APP_APPLETREE_CONTRACT || "0x58665Cf4742476532CA2387Bcc1631Af21C10c79";

  const handleShowDialog = () => {
    if (!walletClient || !publicClient) {
      onError?.("Wallet not connected");
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleRevoke = async () => {
    if (!walletClient || !publicClient) {
      onError?.("Wallet not connected");
      return;
    }

    if (!reason.trim()) {
      onError?.("Please provide a reason for revocation");
      return;
    }

    setShowConfirmDialog(false);
    setRevoking(true);

    try {
      // Step 1: Sign and send on-chain revocation transaction
      setStep("Waiting for wallet signature...");
      console.log("[AdminRevoke] Step 1: Calling decertifyCompany on-chain");
      console.log("[AdminRevoke] Contract:", APPLETREE_CONTRACT);
      console.log("[AdminRevoke] Company wallet:", companyWalletAddress);

      const { request } = await publicClient.simulateContract({
        address: APPLETREE_CONTRACT,
        abi: AppleTreeABI,
        functionName: "decertifyCompany",
        args: [companyWalletAddress],
        account: walletAddress,
      });

      const txHash = await walletClient.writeContract(request);
      console.log("[AdminRevoke] Transaction sent:", txHash);

      // Step 2: Wait for transaction receipt confirmation
      setStep("Waiting for transaction confirmation...");
      console.log("[AdminRevoke] Step 2: Waiting for transaction receipt");
      
      const receipt = await publicClient.waitForTransactionReceipt({ 
        hash: txHash,
        confirmations: 1
      });

      console.log("[AdminRevoke] Transaction confirmed:", receipt);

      // Step 3: Call backend revoke endpoint
      setStep("Confirming revocation...");
      console.log("[AdminRevoke] Step 3: Confirming revocation on backend");
      
      const revokeResponse = await adminApi.revokeCertification(
        requestId,
        txHash,
        reason.trim(),
        adminKey,
        walletAddress
      );

      if (!revokeResponse.success) {
        throw new Error(revokeResponse.error || "Failed to revoke certification");
      }

      console.log("[AdminRevoke] Revocation completed successfully!");
      
      // Step 4: Update UI state
      setStep("Revoked ✅");
      
      onSuccess?.({
        ...revokeResponse,
        receipt
      });

    } catch (error) {
      console.error("[AdminRevoke] Error:", error);
      setStep("");
      
      let errorMessage = "Revocation failed";
      if (error.message?.includes("User rejected")) {
        errorMessage = "Transaction rejected by user";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      onError?.(errorMessage);
    } finally {
      setRevoking(false);
      setReason("");
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setReason("");
    setStep("");
  };

  return (
    <div style={styles.container}>
      <button
        onClick={handleShowDialog}
        disabled={revoking || !walletClient}
        style={{
          ...styles.button,
          opacity: revoking || !walletClient ? 0.6 : 1,
          cursor: revoking || !walletClient ? "not-allowed" : "pointer",
        }}
      >
        {revoking ? (
          <>
            <span style={styles.spinner}>⏳</span>
            {step || "Revoking..."}
          </>
        ) : (
          <>
            <span style={styles.icon}>🗑️</span>
            Revoke / Delete
          </>
        )}
      </button>

      {!walletClient && (
        <div style={styles.warning}>
          ⚠️ Please connect your wallet to revoke
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div style={styles.dialogOverlay}>
          <div style={styles.dialog}>
            <h3 style={styles.dialogTitle}>🗑️ Revoke Certification</h3>
            
            <div style={styles.dialogContent}>
              <div style={styles.dialogRow}>
                <strong>Request ID:</strong>
                <span style={styles.address}>{requestId}</span>
              </div>
              <div style={styles.dialogRow}>
                <strong>Company Wallet:</strong>
                <span style={styles.address}>{companyWalletAddress}</span>
              </div>
              
              <div style={styles.dialogRow}>
                <label htmlFor="revoke-reason" style={styles.label}>
                  <strong>Reason for Revocation: *</strong>
                </label>
                <textarea
                  id="revoke-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Please provide a detailed reason for revoking this certification..."
                  style={styles.textarea}
                  rows={4}
                />
              </div>
            </div>

            <p style={styles.dialogWarning}>
              ⚠️ <strong>WARNING:</strong> This action cannot be undone. 
              You will be asked to sign a blockchain transaction to decertify this company on-chain.
            </p>

            <div style={styles.dialogActions}>
              <button
                onClick={handleCancel}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={!reason.trim()}
                style={{
                  ...styles.revokeButton,
                  opacity: !reason.trim() ? 0.6 : 1,
                  cursor: !reason.trim() ? "not-allowed" : "pointer",
                }}
              >
                Sign & Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    marginTop: "20px",
  },
  button: {
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    justifyContent: "center",
  },
  icon: {
    fontSize: "18px",
  },
  spinner: {
    animation: "spin 1s linear infinite",
  },
  warning: {
    marginTop: "10px",
    padding: "10px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderRadius: "4px",
    fontSize: "13px",
  },
  dialogOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  dialog: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "80vh",
    overflow: "auto",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
  },
  dialogTitle: {
    margin: "0 0 20px 0",
    fontSize: "20px",
    color: "#333",
    borderBottom: "2px solid #dc3545",
    paddingBottom: "10px",
  },
  dialogContent: {
    marginBottom: "20px",
  },
  dialogRow: {
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  address: {
    fontSize: "13px",
    fontFamily: "monospace",
    wordBreak: "break-all",
    color: "#666",
  },
  label: {
    marginBottom: "4px",
  },
  textarea: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
    fontFamily: "inherit",
    resize: "vertical",
  },
  dialogWarning: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px",
    borderRadius: "6px",
    fontSize: "14px",
    marginBottom: "20px",
    border: "1px solid #f5c6cb",
  },
  dialogActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  revokeButton: {
    padding: "10px 20px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
  },
};

export default AdminRevokeButton;
